/** Проигрыш: таймер, реванш, совет — подключение к стору и колбэкам. */
import type { WindowProps } from "../kit";
import { DefeatView } from "./view";
import {
  adminTools,
  appStore,
  closeTab,
  coinsShortActive,
  spendableCoins,
  useStore,
  vipActive,
  registerWindow,
} from "../../../app";
import { actions } from "../actions";
import { balanceV1 } from "@newfungame/engine";
import { playSound } from "../../../audio";
import { useLayout } from "../../desktop/layout";
import { upgradeAdvice } from "./model";
import { useBuy, useStarter } from "../offers";
import { useEffect, useMemo, useState } from "preact/hooks";

/** Секунд до конца таймера по часам сервера; 0 — таймера нет. */
function useLockSeconds(): number {
  const until = useStore(appStore, (s) => (vipActive(s) ? null : (s.player?.retryLock?.until ?? null)));
  const offset = useStore(appStore, (s) => s.clockOffset);
  const [, tick] = useState(0);
  useEffect(() => {
    if (until === null) return;
    const id = setInterval(() => tick((n) => n + 1), 250);
    return () => clearInterval(id);
  }, [until]);
  return until === null ? 0 : Math.max(0, Math.ceil((until - Date.now() - offset) / 1000));
}

export function Defeat({ route, close }: WindowProps<"defeat">) {
  const player = useStore(appStore, (s) => s.player);
  const canPay = useStore(appStore, (s) => s.miniApp);
  const lockSeconds = useLockSeconds();
  const admin = useStore(appStore, adminTools);
  const { busy, buy } = useBuy("defeat");
  const [bought, setBought] = useState(false);
  const starter = useStarter("defeat");
  const shop = balanceV1.season?.shop;
  const locked = lockSeconds > 0;
  const offer = useStore(appStore, (s) => s.player?.revive ?? null);
  // на сайте Stars нет: виден только бесплатный реванш VIP
  const revive = offer && offer.wave === route.wave && (canPay || offer.stars === 0) ? offer : null;
  const phone = useLayout() === "phone";
  // вкладка, открытая в бою, спрятала бы окно: на телефоне они показываются по очереди
  useEffect(() => {
    if (phone) closeTab();
  }, []);
  useEffect(() => {
    if (locked) actions().offerShown("defeat");
  }, [locked]);
  useEffect(() => {
    if (revive) actions().reviveShown(revive);
  }, [revive?.wave, revive?.attempt]);
  // пока окно открыто, покупают и во вкладках: совет идёт за игроком
  // совет — только по монетам, которые сервер уже зачислил (монеты проигранной волны в пути, ISSUE-149)
  const spendable = useStore(appStore, spendableCoins);
  const short = useStore(appStore, coinsShortActive);
  const advice = useMemo(
    () => (player && !short ? upgradeAdvice(balanceV1, { ...player, coins: spendable }) : null),
    [player, spendable, short],
  );
  return (
    <DefeatView
      wave={route.wave}
      rollbackTo={route.rollbackTo}
      coins={player?.coins ?? 0}
      advice={bought ? null : advice}
      lockSeconds={lockSeconds}
      canPay={canPay}
      busy={busy}
      skipStars={player?.prices.skip}
      vipStars={player?.prices.vip}
      revive={revive}
      onRevive={() => buy("revive")}
      starter={starter}
      onStarter={() => buy("starter")}
      onSkip={() => buy("skip")}
      freeSkip={
        player && (player.skipTickets > 0 || (shop !== undefined && player.crystals >= shop.skipCrystals))
          ? { ticket: player.skipTickets > 0, crystals: shop?.skipCrystals ?? 0 }
          : null
      }
      onFreeSkip={() => void actions().spend("skip")}
      onAdminSkip={admin ? () => void actions().adminTool("skip_timer") : undefined}
      onVip={() => buy("vip")}
      onTelegram={() => actions().openTelegram("defeat")}
      onUpgrade={() => {
        if (advice) {
          playSound("purchase");
          actions().upgrade(advice.purchases);
        }
        // во время таймера прокачка без боя: кнопка боя появится, когда таймер кончится
        if (locked) {
          setBought(true);
          return;
        }
        actions().nextWave();
        close();
      }}
      onFight={() => {
        actions().nextWave();
        close();
      }}
    />
  );
}

export function register(): void {
  registerWindow("defeat", Defeat);
}
